import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { useActor } from "@xstate/react";
import { createContext, ReactElement, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { ActorLogicFrom, ActorRefFrom } from "xstate";
import { fastCheckoutMachine } from "./FastCheckoutMachine";

import { getAllCarts } from "@/redux/selectors";
import _get from "lodash/get";
import _isEqual from "lodash/isEqual";

type FastCheckoutContextType = {
  send: ActorRefFrom<ActorLogicFrom<typeof fastCheckoutMachine>>["send"];
  actorRef: ActorRefFrom<typeof fastCheckoutMachine>;
};

export const DEFAULT_SECTION_STATE = {
  account: {
    open: false,
    chevronShow: true,
    errorMessage: "",
  },
  payment: {
    open: false,
    chevronShow: true,
    errorMessage: "",
  },
  shipping: {
    open: false,
    chevronShow: true,
    errorMessage: "",
  },
  delivery: {
    open: false,
    chevronShow: true,
    errorMessage: "",
  },
};

export const FastCheckoutContext =
  createContext<FastCheckoutContextType | null>(null);

export const FastCheckoutMachineProvider = ({
  children,
  orderId,
}: {
  children: ReactElement;
  orderId: string;
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const allCarts = useAppSelector(getAllCarts, _isEqual);
  const retailerIds = allCarts.map((cart) => cart.retailerId);
  const retailerConnectionInfoList = useAppSelector(
    (state) =>
      state.retailers.map((retailer) => {
        const status = _get(state.userProfile, [
          "extendedAttributes",
          `connectedRetailer_${retailer.parentRetailerId}_state`,
        ]);
        return {
          retailerId: retailer.parentRetailerId,
          credential: {
            connectionStatus: status,
          },
        };
      }),
    _isEqual
  );

  const actor = useActor(fastCheckoutMachine, {
    input: {
      context: {
        products: {},
        sectionState: DEFAULT_SECTION_STATE,
        checkoutAllCarts: allCarts,
        navigate,
        dispatch,
        retailerIds,
        retailerConnectionInfoList,
        activeCheckoutRetailerId: retailerIds[0],
        orderId,
      },
    },
  });

  const [, send, actorRef] = actor;

  return (
    <FastCheckoutContext.Provider
      value={{
        send,
        actorRef,
      }}
    >
      {children}
    </FastCheckoutContext.Provider>
  );
};

export const useFastCheckoutContext = () => {
  const fastCheckoutContext = useContext(FastCheckoutContext);
  if (!fastCheckoutContext) {
    throw new Error(
      "useFastCheckout must be used within a FastCheckoutProvider"
    );
  }
  return fastCheckoutContext;
};
